import { useState } from 'react';
import { OknoPotwierdzenia } from '@/components/OknoPotwierdzenia';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowUp, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/toast';
import { useI18n } from '@/lib/i18n';
import { fetchZderzenia, setPermissionLimit, type Zderzenie } from '@/lib/permissions-api';

/**
 * LIMIT NIE PRZEPUŚCI PEŁNEGO WYDANIA — lista blokad, których nie widać z żadnego innego ekranu.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️⚠️ ZGŁOSZONE Z EKSPLOATACJI (30.08.2026): „użytkownik ma uprawnienie do 4 rękawic na dobę,
 *  w szufladzie jest 7 i pełne wydanie — w teorii mógłby pobrać, a dostaje «wydzielanie
 *  przekroczone»". Zderzają się trzy liczby z trzech tabel i nikt nie widzi ich razem: reguła
 *  „pełne wydanie" siedzi na szufladzie, ilość na przegródce, a limit w wydzielaniu.
 *
 *  ⚠️ EKRAN NICZEGO NIE LICZY. Reguła i jej uzasadnienie stoją w instancji
 *  (`zderzenia-pelnego-wydania.ts`); tu jest wyłącznie rysowanie i naprawa. Druga kopia progu
 *  („ostro większe", nie „większe lub równe") rozjechałaby się przy pierwszej zmianie.
 *
 *  ⚠️ WCZYTYWANE OSOBNYM ZAPYTANIEM, nie razem z listą uprawnień: wymaga przejścia CAŁEGO
 *  planogramu wyspy. Lista pojawia się od razu, a ta sekcja dociąga się chwilę później — zamiast
 *  wstrzymywać cały ekran na najdroższym odczycie.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Ile miejsc wypisujemy pod wierszem, zanim przejdziemy na „i N więcej". */
const MIEJSC_NA_EKRANIE = 3;

export function ZderzeniaLimitow(props: { groupId: string; mozeZapisywac: boolean }) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['zderzenia', props.groupId],
    queryFn: () => fetchZderzenia(props.groupId),
  });

  const podnies = useMutation({
    mutationFn: (z: Zderzenie) =>
      setPermissionLimit(z.limit.uprawnienieId, props.groupId, {
        /**
         * ⚠️ OKNO ZOSTAJE BEZ ZMIAN, rusza się WYŁĄCZNIE ilość. Podniesienie limitu i wydłużenie
         * okresu to dwie różne decyzje kadrowe; przycisk ma robić dokładnie to, co obiecuje.
         */
        maxQty: z.najmniejszyDzialajacyLimit,
        period: z.limit.period,
        periodType: z.limit.periodType,
      }),
    onSuccess: async () => {
      toast.success(t('zderz.zlecone'));
      await qc.invalidateQueries({ queryKey: ['zderzenia'] });
      await qc.invalidateQueries({ queryKey: ['permissions'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /**
   * ⚠️ PYTANIE W OKNIE PORTALU, nie w `confirm()` przeglądarki (zgłoszone 02.09.2026).
   * Uzasadnienie stoi w `OknoPotwierdzenia`; tutaj trzymamy tylko, KTÓREGO zderzenia dotyczy —
   * bo pytanie wymienia adresata i liczbę osób w grupie, a te są różne w każdym wierszu.
   *
   * ⚠️ STAN NAD WCZESNYMI `return`ami — ten komponent wychodzi przy błędzie i przy braku
   * zderzeń, a hook postawiony niżej nie wykonałby się w każdym renderze.
   */
  const [doPodniesienia, setDoPodniesienia] = useState<{ z: Zderzenie; pytanie: string } | null>(null);

  /**
   * ⚠️⚠️ BŁĄD WCZYTANIA MÓWIMY WPROST, a nie zwracamy `null`. Poprzednia wersja milczała — i tak
   * właśnie zniknęła blokada, którą instancja wykryła poprawnie: schemat odpowiedzi rozjechał się
   * z instancją, `useQuery` wszedł w `isError`, a panel po prostu się nie narysował. Zgłoszone
   * jako „nigdzie nie widzę tego błędu" (31.08.2026) i to jest gorszy tryb awarii niż brak
   * czujnika: ekran wygląda tak samo, jakby nic nie było nie tak.
   */
  if (q.isError) {
    return (
      <section className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs">
        <p className="flex items-center gap-2 font-medium">
          <AlertTriangle className="size-4 text-destructive" />
          {t('zderz.bladWczytania')}
        </p>
        <p className="mt-1 text-muted-foreground">{(q.error as Error).message}</p>
      </section>
    );
  }
  if (q.isPending) return null;
  const d = q.data;
  if (d.zderzenia.length === 0 && d.niesprawdzone.length === 0) return null;

  const kogo = (z: Zderzenie): string => {
    const a = z.limit.adresat;
    if (a.rodzaj === 'all') return t('zderz.wszyscy');
    if (a.rodzaj === 'group') {
      return a.osob === null
        ? t('zderz.grupa').replace('{nazwa}', a.nazwa)
        : t('zderz.grupaOsob').replace('{nazwa}', a.nazwa).replace('{osob}', String(a.osob));
    }
    return a.nazwa;
  };

  return (
    <section className="rounded-lg border border-[var(--warning)]/40 bg-[var(--warning)]/5 p-3">
      <header className="space-y-1">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <AlertTriangle className="size-4 text-[var(--warning)]" />
          {t('zderz.title')}
          {d.zderzenia.length > 0 && (
            <span className="rounded border border-[var(--warning)]/40 bg-[var(--warning)]/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--warning)]">
              {d.zderzenia.length}
            </span>
          )}
        </h2>
        <p className="text-[11px] leading-snug text-muted-foreground">
          {t('zderz.lead')
            .replace('{limitow}', String(d.limitow))
            .replace('{miejsc}', String(d.miejscPelnegoWydania))}
        </p>
      </header>

      {d.zderzenia.length > 0 && (
        <ul className="mt-3 space-y-2">
          {d.zderzenia.map((z) => {
            const m = z.miejsca[0]!;
            /**
             * ⚠️ PYTAMY, GDY LIMIT JEST GRUPOWY. Podniesienie dotknie każdego członka grupy, więc
             * liczba osób musi stanąć w pytaniu — inaczej jedno kliknięcie zmienia uprawnienia
             * kilkunastu osobom bez ich wymienienia.
             */
            const pytanie = (
              z.limit.adresat.rodzaj === 'group' ? t('zderz.pytanieGrupa') : t('zderz.pytanieOsoba')
            )
              .replace('{n}', String(z.najmniejszyDzialajacyLimit))
              .replace('{okno}', z.limit.periodLabel)
              .replace('{kto}', z.limit.adresat.nazwa)
              .replace('{osob}', z.limit.adresat.osob === null ? '?' : String(z.limit.adresat.osob))
              .replace('{zakres}', z.limit.zakres.nazwa);
            return (
              <li key={z.limit.uprawnienieId} className="rounded border border-border bg-card p-2 text-xs">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 space-y-0.5">
                    <p>
                      <span className="font-medium">{kogo(z)}</span>
                      {' · '}
                      {z.limit.zakres.nazwa}
                    </p>
                    <p className="text-muted-foreground">
                      {t('zderz.limitLinia')
                        .replace('{maxQty}', String(z.limit.maxQty))
                        .replace('{okno}', z.limit.periodLabel)}{' '}
                      <span className="font-mono text-[var(--warning)]">{m.wydanie}</span>{' '}
                      {t('zderz.sztuk')}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {t('zderz.miejsce')
                        .replace('{towar}', m.itemNazwa)
                        .replace('{automat}', m.automat.nazwa)
                        .replace('{vmid}', String(m.automat.vmid))
                        .replace('{pic}', String(m.picNumber))
                        .replace('{szuflada}', String(m.drawerNo))
                        .replace('{przegroda}', String(m.compartmentNo))}
                      {z.miejsca.length > 1 && t('zderz.iInne').replace('{n}', String(z.miejsca.length - 1))}
                    </p>
                    {z.miejsca.length > 1 && (
                      <ul className="pl-3 text-[11px] text-muted-foreground">
                        {z.miejsca.slice(1, 1 + MIEJSC_NA_EKRANIE).map((x) => (
                          <li key={`${x.automat.vmid}-${x.picNumber}-${x.drawerNo}-${x.compartmentNo}`}>
                            {t('zderz.miejsceKrotkie')
                              .replace('{automat}', x.automat.nazwa)
                              .replace('{szuflada}', String(x.drawerNo))
                              .replace('{przegroda}', String(x.compartmentNo))
                              .replace('{wydanie}', String(x.wydanie))}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {/*
                    ⚠️ PRZYCISK PODNOSI LIMIT NA TYM WIERSZU, KTÓRY BLOKUJE — a nie „na osobie".
                    Adresat blokującego limitu (grupa albo osoba) siedzi w tym samym wierszu
                    wydzielania, więc naprawa trafia dokładnie tam, gdzie stoi przeszkoda.
                    Zgłoszone wprost: „musi wykrywać, czy ktoś ma limit z grupy — i wtedy podnosić
                    limit na grupie, czy indywidualny — wtedy tylko danej osobie".
                  */}
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!props.mozeZapisywac || z.limit.tylkoOdczyt || podnies.isPending}
                    title={
                      z.limit.tylkoOdczyt
                        ? t('zderz.tylkoOdczyt')
                        : props.mozeZapisywac
                          ? t('zderz.podniesOpis').replace('{n}', String(z.najmniejszyDzialajacyLimit))
                          : t('zderz.brakRoli')
                    }
                    onClick={() => setDoPodniesienia({ z, pytanie })}
                  >
                    {podnies.isPending ? <Loader2 className="size-4 animate-spin" /> : <ArrowUp className="size-4" />}
                    {t('zderz.podnies').replace('{n}', String(z.najmniejszyDzialajacyLimit))}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {doPodniesienia !== null && (
        <OknoPotwierdzenia
          tytul={t('zderz.podnies').replace('{n}', String(doPodniesienia.z.najmniejszyDzialajacyLimit))}
          tresc={doPodniesienia.pytanie}
          etykietaPotwierdz={t('zderz.podnies').replace('{n}', String(doPodniesienia.z.najmniejszyDzialajacyLimit))}
          pracuje={podnies.isPending}
          onPotwierdz={() => {
            podnies.mutate(doPodniesienia.z);
            setDoPodniesienia(null);
          }}
          onAnuluj={() => setDoPodniesienia(null)}
        />
      )}

      {/*
        ⚠️ CZEGO CZUJNIK NIE SPRAWDZA — MÓWIMY WPROST. Klasa limitów cicho pomijana uczy, że
        „portal by pokazał, gdyby coś było", czyli jest gorsza niż brak czujnika.
      */}
      {d.niesprawdzone.length > 0 && (
        <div className="mt-3 border-t border-border pt-2">
          <p className="text-[11px] font-medium">
            {t('zderz.niesprawdzone').replace('{n}', String(d.niesprawdzone.length))}
          </p>
          <ul className="mt-0.5 space-y-0.5 text-[11px] text-muted-foreground">
            {d.niesprawdzone.slice(0, 5).map((n) => (
              <li key={`${n.cel}-${n.zakres}`}>
                {n.cel} → {n.zakres} · {n.powod}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
